// components/HeroBit.js (Updated)
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { designTokens } from '../../theme/designTokens';
import { Play, Sparkles, ArrowDown } from 'lucide-react';
import LuxuryButton from '../ui/LuxuryButton';
import { Reveal } from "../animations/Reveal";
import API_BASE from "../../api/api";
import LuxuryLoading from "../ui/LuxuryLoading";

const HeroBit = () => {
    const [heroSlides, setHeroSlides] = useState([]);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPlaying] = useState(true);
    const [loading, setLoading] = useState(true);
    const containerRef = useRef(null);
    const intervalRef = useRef(null);
    const { scrollYProgress } = useScroll();

    // Parallax effect
    const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 100]);

    // Fetch hero slides from backend
    useEffect(() => {
        const fetchHeroSlides = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_BASE}/hero`);
                const data = await response.json();

                if (data.success) {
                    setHeroSlides(data.data);
                    if (data.data.length > 0) {
                        setCurrentSlide(0);
                    }
                }
            } catch (error) {
                console.error('Error fetching hero slides:', error);
                // Fallback to empty slides - you could show an error state
            } finally {
                setLoading(false);
            }
        };

        fetchHeroSlides();
    }, []);

    // Auto-slide effect
    useEffect(() => {
        if (!isPlaying || heroSlides.length <= 1) return;

        intervalRef.current = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 4000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isPlaying, heroSlides.length]);

    // Track CTA clicks
    const trackClick = async (slideId, action) => {
        try {
            await fetch(`/api/hero/${slideId}/track-click`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action })
            });

            // You can also track in your analytics service
            console.log(`Tracked ${action} click for slide ${slideId}`);
        } catch (error) {
            console.error('Error tracking click:', error);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-pearl flex flex-col items-center justify-center">
                <div className="text-center">
                    <LuxuryLoading size="large" />
                </div>
            </div>
        );
    }

    // No slides state
    if (!heroSlides.length) {
        return (
            <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pearl to-nude">
                <div className="text-center max-w-md mx-auto px-4">
                    <h1 className="text-3xl font-display text-charcoal mb-4">Welcome to KROMAIC</h1>
                    <p className="text-charcoal/70 mb-6">No hero slides available at the moment.</p>
                    <LuxuryButton variant="primary">Explore Collections</LuxuryButton>
                </div>
            </section>
        );
    }

    const currentSlideData = heroSlides[currentSlide];

    // Animation variants (same as before)
    const slideVariants = {
        enter: (direction) => ({
            x: direction > 0 ? 1000 : -1000,
            opacity: 0
        }),
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1
        },
        exit: (direction) => ({
            zIndex: 0,
            x: direction < 0 ? 1000 : -1000,
            opacity: 0
        })
    };

    const contentVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: designTokens.animations.easings.luxury
            }
        }
    };

    const imageVariants = {
        hidden: {
            opacity: 0,
            scale: 0.9,
            rotate: -5
        },
        visible: {
            opacity: 1,
            scale: 1,
            rotate: 0,
            transition: {
                duration: 1,
                ease: designTokens.animations.easings.luxury
            }
        }
    };

    return (
        <section
            ref={containerRef}
            className="
                relative overflow-hidden
                min-h-screen
                pt-24 sm:pt-22 md:pt-26 lg:pt-32 xl:pt-32
                pb-20 sm:pb-24 md:pb-32 lg:pb-0
            "
            style={{ background: currentSlideData.gradient }}
        >
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                {/* Floating Particles */}
                {[...Array(15)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-[3px] h-[3px] rounded-full opacity-40"
                        style={{ background: currentSlideData.productColor }}
                        animate={{
                            y: [0, -80, 0],
                            x: [0, Math.random() * 40 - 20, 0],
                            opacity: [0.2, 0.6, 0.2],
                        }}
                        transition={{
                            duration: 5 + Math.random() * 4,
                            repeat: Infinity,
                            delay: Math.random() * 2,
                            ease: "easeInOut"
                        }}
                    />
                ))}

                {/* Gradient Orbs */}
                <motion.div
                    className="absolute top-1/5 left-1/10 w-[150px] sm:w-[200px] md:w-[300px] h-[150px] sm:h-[200px] md:h-[300px] rounded-full blur-[20px] sm:blur-[30px] md:blur-[40px] hidden sm:block"
                    style={{
                        background: `radial-gradient(circle, ${currentSlideData.productColor}15 0%, transparent 70%)`,
                    }}
                    animate={{
                        scale: [1, 1.3, 1],
                        x: [0, 40, 0],
                        y: [0, -20, 0],
                    }}
                    transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                />

                {/* Second Gradient Orb */}
                <motion.div
                    className="absolute bottom-1/5 right-1/10 w-[100px] sm:w-[150px] md:w-[200px] h-[100px] sm:h-[150px] md:h-[200px] rounded-full blur-[15px] sm:blur-[20px] md:blur-[30px] hidden sm:block"
                    style={{
                        background: `radial-gradient(circle, ${currentSlideData.accentColor}10 0%, transparent 70%)`,
                    }}
                    animate={{
                        scale: [1, 1.4, 1],
                        x: [0, -30, 0],
                        y: [0, 20, 0],
                    }}
                    transition={{
                        duration: 18,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1,
                    }}
                />

                {/* Parallax Text Background */}
                <motion.div
                    className="absolute bottom-0 left-0 right-0 pointer-events-none overflow-hidden z-0"
                    style={{ y: parallaxY }}
                >
                    <div className="container-luxury">
                        <div className="text-center">
                            <span className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-display font-bold tracking-wider bg-gradient-to-r from-champagne/20 via-champagne/15 to-champagne/10 bg-clip-text text-transparent blur-[0.5px] sm:blur-[1px] md:blur-[2px] opacity-40 select-none">
                                KROMAIC
                            </span>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Slide Content */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentSlide}
                    custom={1}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                        x: { type: "spring", stiffness: 300, damping: 30 },
                        opacity: { duration: 0.4 }
                    }}
                    className="
                        relative z-10
                        max-w-7xl mx-auto
                        px-4 sm:px-5 md:px-6 lg:px-8
                        h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)] md:h-[calc(100vh-6rem)] lg:h-[90vh] xl:h-[85vh] 2xl:h-[80vh]
                        flex flex-col lg:flex-row
                        items-center justify-between
                        gap-8 sm:gap-10 md:gap-12 lg:gap-16 xl:gap-20
                    ">
                    {/* Left Content */}
                    <div className="w-full lg:w-1/2 xl:w-7/12 2xl:w-6/12 max-w-3xl mx-auto lg:mx-0 lg:max-w-none mt-6 sm:mt-8 md:mt-10 lg:mt-0 pb-8 sm:pb-10 md:pb-12 lg:pb-0">
                        <motion.div
                            variants={contentVariants}
                            initial="hidden"
                            animate="visible"
                            className="flex flex-col gap-3 sm:gap-4 md:gap-5 lg:gap-6 text-center lg:text-left"
                        >
                            {/* Badge */}
                            <motion.div
                                variants={contentVariants}
                                className="inline-flex items-center gap-2 px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 md:py-2.5 rounded-full backdrop-blur-sm border border-white/25 mx-auto lg:mx-0"
                                style={{
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    width: 'fit-content',
                                }}
                            >
                                <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" color={currentSlideData.productColor} />
                                <span className="text-xs sm:text-sm md:text-sm font-medium tracking-wider uppercase"
                                      style={{ color: designTokens.colors.text.primary }}>
                                    {currentSlideData.badge}
                                </span>
                            </motion.div>

                            {/* Title */}
                            <div className="mt-2 sm:mt-3 md:mt-4 lg:mt-0 text-center lg:text-left">
                                {/* LINE 1 : Title + Subtitle */}
                                <motion.h1
                                    variants={contentVariants}
                                    className="
                                        font-display font-light tracking-tight
                                        leading-[1.05] sm:leading-[1.1] md:leading-[1.15]
                                        mb-0.5 sm:mb-1
                                        text-2xl sm:text-2xl md:text-5xl lg:text-5xl xl:text-6xl 2xl:text-7xl
                                    "
                                    style={{ color: designTokens.colors.text.primary }}
                                >
                                    {currentSlideData.title} {currentSlideData.subtitle}
                                </motion.h1>

                                {/* LINE 2 : Highlight */}
                                <motion.h2
                                    variants={contentVariants}
                                    className="
                                        font-display font-light italic
                                        leading-[1.05] sm:leading-[1.1] md:leading-[1.15]
                                        text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl
                                    "
                                    style={{
                                        color: currentSlideData.accentColor,
                                        textShadow: `0 4px 20px ${currentSlideData.accentColor}40`,
                                    }}
                                >
                                    {currentSlideData.highlight}
                                </motion.h2>
                            </div>

                            {/* Description */}
                            <motion.div
                                variants={contentVariants}
                                className="max-w-2xl mx-auto lg:mx-0 mt-1.5 sm:mt-2 md:mt-2.5 lg:mt-0"
                            >
                                <p className="text-sm sm:text-base md:text-lg lg:text-xl text-charcoal/70 leading-relaxed sm:leading-relaxed md:leading-relaxed font-light">
                                    {currentSlideData.description}
                                </p>
                            </motion.div>

                            {/* CTA Buttons */}
                            <motion.div
                                variants={contentVariants}
                                className="flex flex-wrap gap-2 sm:gap-2.5 md:gap-3 lg:gap-4 justify-center lg:justify-start pt-1.5 sm:pt-2 md:pt-2.5 lg:pt-2"
                            >
                                <LuxuryButton
                                    variant="primary"
                                    icon={<Play size={16} />}
                                    iconPosition="right"
                                    style={{
                                        background: designTokens.colors.gradients.champagne,
                                    }}
                                    onClick={() => {
                                        trackClick(currentSlideData.id, 'primary');
                                        // Navigate to product page or collection
                                        if (currentSlideData.productData) {
                                            window.location.href = `/products/${currentSlideData.productData.id}`;
                                        }
                                    }}
                                >
                                    {currentSlideData.ctaPrimary}
                                </LuxuryButton>

                                <LuxuryButton
                                    variant="secondary"
                                    icon={<Play size={16} />}
                                    style={{
                                        borderColor: currentSlideData.accentColor,
                                        color: currentSlideData.accentColor,
                                    }}
                                    onClick={() => {
                                        trackClick(currentSlideData.id, 'secondary');
                                        window.location.href = '/story';
                                    }}
                                >
                                    {currentSlideData.ctaSecondary}
                                </LuxuryButton>
                            </motion.div>

                            {/* Special Offers for Mobile */}
                            <div className="block lg:hidden mt-4 sm:mt-5 md:mt-6 pt-4 sm:pt-5 md:pt-6 border-t border-white/20">
                                <Reveal>
                                    <div className="flex flex-col gap-2 sm:gap-2.5 md:gap-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs sm:text-sm md:text-sm text-charcoal/70">Limited Time Offer</span>
                                            <span className="text-xs sm:text-sm md:text-sm font-medium text-champagne">30% OFF</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs sm:text-sm md:text-sm text-charcoal/70">Free Shipping</span>
                                            <span className="text-xs sm:text-sm md:text-sm font-medium text-sage">Worldwide</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs sm:text-sm md:text-sm text-charcoal/70">Sample Kit</span>
                                            <span className="text-xs sm:text-sm md:text-sm font-medium text-champagne">Free Gift</span>
                                        </div>
                                    </div>
                                </Reveal>
                            </div>

                            {/* Stats */}
                            <motion.div
                                variants={contentVariants}
                                className="flex flex-wrap gap-3 sm:gap-4 md:gap-5 lg:gap-6 xl:gap-8 justify-center lg:justify-start pt-4 sm:pt-5 md:pt-6 lg:pt-8 xl:pt-10"
                            >
                                {[
                                    { value: '98%', label: 'Naturally Derived' },
                                    { value: '45+', label: 'Ingredients' },
                                    { value: '3', label: 'Continents' },
                                    { value: '∞', label: 'Possibilities' },
                                ].map((stat, index) => (
                                    <div key={index} className="flex flex-col items-center lg:items-start">
                                        <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-light leading-none"
                                             style={{ color: currentSlideData.productColor }}>
                                            {stat.value}
                                        </div>
                                        <div className="text-xs sm:text-sm md:text-sm tracking-wider uppercase mt-0.5 sm:mt-1 md:mt-1.5 lg:mt-2"
                                             style={{ color: designTokens.colors.text.tertiary }}>
                                            {stat.label}
                                        </div>
                                    </div>
                                ))}
                            </motion.div>
                        </motion.div>
                    </div>

                    {/* Right Product Visual */}
                    <motion.div
                        variants={imageVariants}
                        initial="hidden"
                        animate="visible"
                        className="w-full lg:w-1/2 xl:w-5/12 2xl:w-6/12 relative mt-6 sm:mt-8 md:mt-10 lg:mt-0 hidden lg:block"
                    >
                        <div className="relative w-full h-[280px] sm:h-[320px] md:h-[380px] lg:h-[420px] xl:h-[480px] 2xl:h-[520px]">
                            {/* Product Container */}
                            <div className="absolute inset-0 rounded-2xl sm:rounded-3xl backdrop-blur-[15px] sm:backdrop-blur-[20px] overflow-hidden"
                                 style={{
                                     background: currentSlideData.productGradient,
                                     border: `1px solid rgba(255, 255, 255, 0.15)`,
                                     boxShadow: `0 20px 40px rgba(0, 0, 0, 0.1), 0 0 30px ${currentSlideData.productColor}20`,
                                 }}>
                                {/* Animated Inner Glow */}
                                <motion.div
                                    className="absolute inset-3 sm:inset-4 md:inset-5 lg:inset-6 rounded-xl sm:rounded-2xl"
                                    style={{
                                        background: `linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.15) 100%)`,
                                    }}
                                    animate={{
                                        boxShadow: [
                                            `inset 0 0 60px ${currentSlideData.productColor}15`,
                                            `inset 0 0 90px ${currentSlideData.productColor}25`,
                                            `inset 0 0 60px ${currentSlideData.productColor}15`,
                                        ],
                                    }}
                                    transition={{
                                        duration: 5,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                    }}
                                />

                                {/* Product Image */}
                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full flex items-center justify-center">
                                    <motion.div
                                        className="filter drop-shadow-lg"
                                        style={{
                                            color: currentSlideData.productColor,
                                            filter: `drop-shadow(0 8px 20px ${currentSlideData.productColor}40)`,
                                        }}
                                        animate={{
                                            scale: [1, 1.05, 1],
                                            rotate: [0, 5, 0, -5, 0],
                                        }}
                                        transition={{
                                            duration: 8,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                        }}
                                    >
                                        {currentSlideData.productIcon ? (
                                            <img
                                                src={currentSlideData.productIcon}
                                                alt={currentSlideData.productName}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "contain"
                                                }}
                                                draggable={false}
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <div className="text-center">
                                                    <div className="text-4xl mb-2">✨</div>
                                                    <p className="text-sm text-charcoal/70">Product Image</p>
                                                </div>
                                            </div>
                                        )}
                                    </motion.div>
                                </div>

                                {/* Floating Bubbles */}
                                {[...Array(3)].map((_, i) => (
                                    <motion.div
                                        key={i}
                                        className="absolute rounded-full filter blur-[4px] sm:blur-[5px]"
                                        style={{
                                            width: `${20 + i * 15}px`,
                                            height: `${20 + i * 15}px`,
                                            background: `radial-gradient(circle, ${currentSlideData.productColor}${15 - i * 5} 0%, transparent 70%)`,
                                            left: `${15 + i * 20}%`,
                                            top: `${10 + i * 15}%`,
                                        }}
                                        animate={{
                                            y: [0, -20, 0],
                                            x: [0, 15, 0],
                                            scale: [1, 1.2, 1],
                                        }}
                                        transition={{
                                            duration: 4 + i,
                                            repeat: Infinity,
                                            delay: i * 0.5,
                                            ease: "easeInOut",
                                        }}
                                    />
                                ))}
                            </div>

                            {/* Product Label */}
                            <motion.div
                                className="
                                    absolute
                                    -bottom-3 sm:-bottom-4 md:-bottom-5 lg:-bottom-6 xl:-bottom-7
                                    left-1/2 -translate-x-1/2
                                    px-3 sm:px-4 md:px-5 lg:px-6 xl:px-7
                                    py-2 sm:py-2.5 md:py-3 lg:py-4 xl:py-5
                                    min-w-[120px] sm:min-w-[140px] md:min-w-[160px] lg:min-w-[180px] xl:min-w-[200px]
                                    bg-white/95
                                    backdrop-blur-[15px] sm:backdrop-blur-[20px]
                                    rounded-lg sm:rounded-xl
                                    border
                                    text-center
                                "
                                style={{
                                    border: `1px solid ${designTokens.colors.border.light}`,
                                    boxShadow: designTokens.shadows.luxury,
                                }}
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.8, duration: 1 }}
                            >
                                <div
                                    className="
                                        text-[9px] sm:text-[10px] md:text-xs lg:text-sm
                                        tracking-widest
                                        uppercase
                                        mb-0.5 sm:mb-1 md:mb-1.5 lg:mb-2
                                    "
                                    style={{ color: designTokens.colors.text.tertiary }}
                                >
                                    {currentSlideData.productLabel}
                                </div>

                                <div
                                    className="
                                        text-base sm:text-lg md:text-xl lg:text-2xl
                                        font-normal
                                    "
                                    style={{
                                        color: designTokens.colors.text.primary,
                                        fontFamily: designTokens.typography.fontFamilies.display,
                                    }}
                                >
                                    {currentSlideData.productName}
                                </div>

                                {/* Show price if product data exists */}
                                {currentSlideData.productData && (
                                    <div className="mt-1">
                                        {currentSlideData.productData.discount > 0 ? (
                                            <>
                                                <span className="text-sm text-sage font-medium line-through mr-2">
                                                    ${currentSlideData.productData.price}
                                                </span>
                                                <span className="text-lg text-champagne font-bold">
                                                    ${currentSlideData.productData.discountPrice}
                                                </span>
                                            </>
                                        ) : (
                                            <span className="text-lg text-charcoal font-medium">
                                                ${currentSlideData.productData.price}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </motion.div>
                        </div>
                    </motion.div>
                </motion.div>
            </AnimatePresence>

            {/* Slide Indicators */}
            <div className="absolute bottom-24 sm:bottom-28 md:bottom-32 lg:bottom-12 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
                {heroSlides.map((_, index) => (
                    <button
                        key={index}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'w-6 bg-champagne' : 'bg-white/50 hover:bg-white/70'}`}
                        onClick={() => setCurrentSlide(index)}
                        aria-label={`Go to slide ${index + 1}`}
                    />
                ))}
            </div>

            {/* Mobile Offers Panel */}
            <div className="block lg:hidden fixed bottom-0 left-0 right-0 bg-gradient-to-t from-white/95 via-white/90 to-white/85 backdrop-blur-sm border-t border-white/30 py-2.5 sm:py-3 px-3 sm:px-4 z-30">
                <div className="flex justify-between items-center">
                    <div className="flex-1">
                        <div className="text-xs sm:text-sm md:text-sm text-charcoal/70">Current Offers:</div>
                        <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3 mt-0.5 sm:mt-1">
                            <span className="text-xs sm:text-sm md:text-sm font-medium bg-champagne/20 text-champagne px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">30% OFF</span>
                            <span className="text-xs sm:text-sm md:text-sm font-medium bg-sage/20 text-sage px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">Free Gift</span>
                            <span className="text-xs sm:text-sm md:text-sm font-medium bg-sage/20 text-sage px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">Free Shipping</span>
                        </div>
                    </div>
                    <LuxuryButton
                        variant="primary"
                        size="small sm:medium"
                        className="ml-2 sm:ml-3 md:ml-4"
                        onClick={() => window.location.href = '/products'}
                    >
                        Shop Now
                    </LuxuryButton>
                </div>
            </div>

            {/* Scroll Indicator */}
            <motion.div
                className="absolute bottom-12 sm:bottom-14 md:bottom-16 lg:bottom-8 left-1/2 transform -translate-x-1/2 z-20"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 1 }}
            >
                <motion.div
                    animate={{ y: [0, 6, 0] }}
                    transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                    className="flex flex-col items-center gap-0.5 sm:gap-1 md:gap-1.5 lg:gap-2 cursor-pointer"
                    onClick={() => {
                        const section = document.getElementById('story');
                        if (section) {
                            section.scrollIntoView({ behavior: 'smooth' });
                        }
                    }}
                >
                    <span className="text-xs sm:text-sm md:text-sm tracking-widest uppercase font-medium"
                          style={{ color: designTokens.colors.text.tertiary }}>
                        Discover More
                    </span>
                    <ArrowDown className="w-4 h-4 sm:w-4.5 sm:h-4.5 md:w-5 md:h-5" color={currentSlideData.productColor} />
                </motion.div>
            </motion.div>
        </section>
    );
};

export default HeroBit;